import * as React from 'react';
import {
  MasonryEngineNode,
  MasonryEngineNodeAction,
  MasonryEngineNodeRendererProps,
  useStateControllerStore,
} from '@leagueplatform/masonry-engine';
import {
  Box,
  GDSHeadingLevel,
  HeadingText,
  Image,
  ParagraphText,
  StackLayout,
} from '@leagueplatform/genesis-core';
import {
  parseDefaultAddressString,
  resolveTemplate,
} from './utils/order-new-card-address-summary-card.utils';

export const NODE_TYPE_ORDER_NEW_CARD_ADDRESS_SUMMARY_CARD =
  'orderNewCardAddressSummaryCard' as const;

type OrderNewCardAddressSummaryCardProps = {
  image?: {
    url: string;
    description?: string;
    width?: string;
    height?: string;
  };
  heading: {
    heading: string;
    headingLevel?: GDSHeadingLevel;
  };
  formId: string;
  inputIds: string[];
  addressLine1: string;
  addressLine2?: string;
};

export type OrderNewCardAddressSummaryCardNodeActions = {
  onLoad?: MasonryEngineNodeAction;
};

export type OrderNewCardAddressSummaryCardNode = MasonryEngineNode<
  typeof NODE_TYPE_ORDER_NEW_CARD_ADDRESS_SUMMARY_CARD,
  OrderNewCardAddressSummaryCardProps,
  OrderNewCardAddressSummaryCardNodeActions
>;

export type OrderNewCardAddressSummaryCardRendererProps =
  MasonryEngineNodeRendererProps<OrderNewCardAddressSummaryCardNode>;

const formatClassNames = (...classNames: (string | undefined)[]) =>
  classNames?.filter(Boolean).join(' ') || '';

/**
 * Masonry renderer for `orderNewCardAddressSummaryCard`.
 *
 * - **Source of truth**: reads form field values from the Masonry state store via `formId`/`inputIds`.
 * - **Template display**: `addressLine1` and `addressLine2` are template strings; `{token}` is replaced
 *   by a value whose key matches `token`.
 * - **Mode switch** (`selectedAddress`):
 *   - When `selectedAddress === "CUSTOM_ADDRESS"`, `{{token}}` values come from the Masonry form fields.
 *   - Otherwise, `selectedAddress` is treated as a default-address string of the form
 *     `key=value?key2=value2`, where values may contain `+` for spaces; those parsed values are used
 *     to resolve `{{token}}`.
 */
export const OrderNewCardAddressSummaryCardRenderer = ({
  image,
  heading,
  formId,
  inputIds,
  addressLine1,
  addressLine2,
  rootElementRef,
  engineClassName,
  className,
  locatorId,
}: OrderNewCardAddressSummaryCardRendererProps) => {
  const selectedInputValuesById = useStateControllerStore((store) => {
    const fieldNodeIds = store.getFormFieldNodesMap(formId) || [];

    const valuesByFormFieldId = new Map<string, unknown>();

    fieldNodeIds.forEach((fieldNodeId) => {
      const nodeState = store.getNodeState<any>(fieldNodeId);
      const formFieldId = nodeState?.formFieldProperties?.formFieldId;
      if (typeof formFieldId === 'string') {
        valuesByFormFieldId.set(formFieldId, nodeState?.value);
      }
    });

    // Preserve inputIds ordering
    return inputIds.reduce<Record<string, unknown>>((acc, inputId) => {
      acc[inputId] = valuesByFormFieldId.get(inputId);
      return acc;
    }, {});
  });

  const { selectedAddress } = selectedInputValuesById as {
    selectedAddress?: unknown;
  };

  const templateValuesById = React.useMemo(() => {
    if (
      typeof selectedAddress === 'string' &&
      selectedAddress &&
      selectedAddress !== 'CUSTOM_ADDRESS'
    ) {
      const parsed = parseDefaultAddressString(selectedAddress);
      return Object.keys(parsed).length > 0 ? parsed : selectedInputValuesById;
    }

    return selectedInputValuesById;
  }, [selectedAddress, selectedInputValuesById]);

  const resolvedAddressLine1 = React.useMemo(
    () => resolveTemplate(addressLine1, templateValuesById),
    [addressLine1, templateValuesById],
  );
  const resolvedAddressLine2 = React.useMemo(
    () =>
      addressLine2 ? resolveTemplate(addressLine2, templateValuesById) : '',
    [addressLine2, templateValuesById],
  );

  return (
    <Box
      ref={rootElementRef}
      className={formatClassNames(engineClassName, className)}
      data-testid={locatorId}
      css={{
        borderStyle: 'solid',
        borderColor: '$onSurfaceBorderSubdued',
        borderRadius: '$card',
        borderWidth: '$thin',
        padding: '$one',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        gap: '$half',
      }}
    >
      {image?.url ? (
        <Image
          src={image.url}
          alt={image.description ?? ''}
          width={image.width ?? '48'}
          height={image.height ?? '48'}
        />
      ) : null}

      <StackLayout spacing="$none">
        <HeadingText level={heading.headingLevel ?? '4'} size="sm">
          {heading.heading}
        </HeadingText>

        <ParagraphText size="sm">
          {resolvedAddressLine1}
          {resolvedAddressLine2 ? (
            <Box as="span" css={{ display: 'block' }}>
              {resolvedAddressLine2}
            </Box>
          ) : null}
        </ParagraphText>
      </StackLayout>
    </Box>
  );
};
